import type { Map as LeafletMap } from 'leaflet';

import {
  roundCoordinate,
  type MapPickerHandle,
  type MapPickerOptions,
  type MapProvider,
} from './mapProvider';

/**
 * OpenStreetMap, drawn by Leaflet. The only file that knows either exists.
 *
 * ATTRIBUTION IS NOT OPTIONAL. OSM's tiles are free to use and their licence
 * requires the credit to be visible on the map — Leaflet's default control is
 * kept for exactly that reason, and removing it to tidy the corner would put
 * us in breach.
 *
 * Their tile servers are run on donations and have a usage policy: fine for a
 * shop's checkout, not fine for bulk or heavy automated traffic. If this ever
 * carries real volume, move to a paid tile host — which is a URL change here
 * and nothing else.
 *
 * LEAFLET IS LOADED ON DEMAND. It is ~150KB with its stylesheet and only the
 * address step needs it; importing it at the top of the module would put it in
 * the bundle every customer downloads to look at the home page.
 */
const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
/** Their policy: do not request deeper than this from the public servers. */
const MAX_ZOOM = 19;

let stylesheetAdded = false;

/**
 * Leaflet ships a stylesheet its layout genuinely needs — without it the tiles
 * stack vertically instead of tiling. Injected once, here, rather than
 * imported globally so a customer who never opens the map never fetches it.
 */
function ensureStylesheet(): void {
  if (stylesheetAdded || typeof document === 'undefined') return;
  const existing = document.querySelector('link[data-leaflet-css]');
  if (existing) {
    stylesheetAdded = true;
    return;
  }
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = new URL('leaflet/dist/leaflet.css', import.meta.url).href;
  link.setAttribute('data-leaflet-css', 'true');
  document.head.append(link);
  stylesheetAdded = true;
}

export const osmMapProvider: MapProvider = {
  name: 'openstreetmap',

  async create(container: HTMLElement, options: MapPickerOptions): Promise<MapPickerHandle> {
    ensureStylesheet();
    const leaflet = await import('leaflet');

    const map: LeafletMap = leaflet.map(container, {
      center: [options.center.lat, options.center.lng],
      zoom: options.zoom,
      // No rotation, no 3D, nothing to get lost in: this control exists to
      // answer one question, which is "which building".
      zoomControl: true,
      attributionControl: true,
      // OFF until the customer touches the map — see below. Everything else is
      // Leaflet's default and stays on: drag to pan, pinch to zoom, double-tap
      // to zoom in, arrow keys to pan and +/- to zoom for anyone on a keyboard.
      scrollWheelZoom: false,
    });

    // Scroll-wheel zoom is enabled only after a deliberate interaction, and
    // switched off again when the map loses focus.
    //
    // A map that grabs the wheel is a trap: this sits in the middle of a
    // checkout form, and a customer scrolling the page with the cursor over it
    // would stop dead and zoom instead. A map that NEVER zooms on the wheel is
    // just as wrong on a laptop, where that is the gesture people reach for.
    // Clicking or focusing it first is the compromise every embedded map ends
    // up at.
    const enableWheel = () => map.scrollWheelZoom.enable();
    const disableWheel = () => map.scrollWheelZoom.disable();
    map.on('focus', enableWheel);
    map.on('click', enableWheel);
    map.on('blur', disableWheel);
    map.on('mouseout', disableWheel);

    leaflet.tileLayer(TILE_URL, { attribution: ATTRIBUTION, maxZoom: MAX_ZOOM }).addTo(map);

    // The current zoom, on the container. Leaflet keeps old tiles on screen
    // while new ones load, so the tile URLs are not a reliable reading of
    // where the map actually is — this is, and anything watching the map
    // (styling, diagnostics, a test) can read it without reaching inside.
    const reportZoom = () => {
      container.dataset['zoom'] = String(map.getZoom());
    };
    reportZoom();
    map.on('zoomend', reportZoom);

    // THE PIN IS THE CENTRE OF THE MAP, not a draggable marker. Dragging a
    // marker on a touch screen fights the gesture that pans the map, and the
    // customer ends up moving the map when they meant the pin. Moving the map
    // under a fixed crosshair is one gesture with one meaning — it is how
    // every ride-hailing app asks this question, so it needs no explaining.
    // Only a move the CUSTOMER made is an answer.
    //
    // `setCenter` fires Leaflet's `moveend` like any other move, so without
    // this a programmatic recentre — opening on the PIN code's area, or
    // jumping to a device fix — reported itself as the customer placing a pin.
    // The visible symptom was a checkout that let you through having silently
    // adopted a postal centroid a kilometre from the door. The caller reports
    // its own moves explicitly, because only the caller knows what they mean.
    let programmatic = false;

    const report = () => {
      if (programmatic) {
        programmatic = false;
        return;
      }
      const centre = map.getCenter();
      options.onCenterChanged({
        lat: roundCoordinate(centre.lat),
        lng: roundCoordinate(centre.lng),
      });
    };
    map.on('moveend', report);

    return {
      setCenter(next, zoom) {
        // Silent: see `programmatic` above. Whoever calls this decides whether
        // it counts as an answer and says so itself.
        programmatic = true;
        map.setView([next.lat, next.lng], zoom ?? map.getZoom());
      },
      destroy() {
        map.off('moveend', report);
        map.off('zoomend', reportZoom);
        map.off('focus', enableWheel);
        map.off('click', enableWheel);
        map.off('blur', disableWheel);
        map.off('mouseout', disableWheel);
        map.remove();
      },
    };
  },
};
